import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Ticket, PencilLine, Send, MailCheck, ListChecks, Mail, X } from 'lucide-react';

const SEEN_KEY = 'aes-onboarding-seen-v1';
const OPEN_EVENT = 'aes:open-onboarding';

/** Re-open the welcome guide from anywhere (e.g. a "How it works" link). */
export function openOnboarding() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

const STEPS = [
  {
    icon: Ticket,
    title: '1. Start a ticket',
    body: 'Click the blue + New Ticket button in the top corner. That opens a short form.'
  },
  {
    icon: PencilLine,
    title: '2. Tell us what’s wrong',
    body: 'Pick a category, give it a short title, and describe what happened. A screenshot helps a lot — you can paste one right into the box.'
  },
  {
    icon: Send,
    title: '3. Send it',
    body: 'Hit Submit. You’ll get an email confirming we received it, with a ticket number like #1042.'
  },
  {
    icon: MailCheck,
    title: '4. We take it from here',
    body: 'IT picks up your ticket. If we need more info, we’ll email you — just reply to that email (or answer right here in the ticket).'
  },
  {
    icon: ListChecks,
    title: '5. Track the progress',
    body: 'Open “My Tickets” any time to see the status: Pending → In Progress → Completed when it’s done.'
  }
];

/**
 * First-visit welcome guide. Shows once (gated by localStorage), explains how
 * to open a ticket and what happens next in plain language. Re-openable via
 * openOnboarding() — wired to the "How it works" link on My Tickets.
 *
 * Mounted once in ProtectedLayout so it can appear over any authenticated page.
 */
export function OnboardingDialog() {
  const [open, setOpen] = useState(false);

  // First visit → show automatically.
  useEffect(() => {
    try {
      if (!localStorage.getItem(SEEN_KEY)) setOpen(true);
    } catch {
      /* private mode / storage blocked — just don't auto-show */
    }
  }, []);

  // Allow re-opening from elsewhere.
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(OPEN_EVENT, handler);
    return () => window.removeEventListener(OPEN_EVENT, handler);
  }, []);

  // Esc to close + lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* ignore */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="onboarding-title"
      onClick={close}
    >
      <div
        className="relative w-full max-w-lg my-4 rounded-xl border bg-card text-card-foreground shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute right-3 top-3 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-6">
          <h2 id="onboarding-title" className="text-xl font-semibold">
            Welcome to the AES I.T. Helpdesk 👋
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            This is where you ask IT for help. Here’s how it works — it only takes a minute.
          </p>

          <ol className="mt-5 space-y-4">
            {STEPS.map((s) => (
              <li key={s.title} className="flex gap-3">
                <div className="shrink-0 w-9 h-9 rounded-lg bg-primary/10 text-primary grid place-items-center">
                  <s.icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium text-sm">{s.title}</div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-5 flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5 text-sm">
            <Mail className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <span>
              In a hurry? You can also just email{' '}
              <span className="font-medium">helpdesk@aesintl.com</span> and we’ll open a ticket for you automatically.
            </span>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-2 sm:justify-end">
            <Link
              to="/new-ticket"
              onClick={close}
              className="inline-flex items-center justify-center gap-1.5 rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              <Ticket className="w-4 h-4" /> Create my first ticket
            </Link>
            <button
              type="button"
              onClick={close}
              className="rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:opacity-90"
            >
              Got it — let’s go
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
